/**
 * QuickLogPanel (F3) — fast few-tap test-result entry that shares its
 * history with the guided Test Kit Assistant.
 *
 * Flow: tap a parameter (Alkalinity / Calcium / … / pH) → pick the test
 * kit (remembered per parameter on the Pi) → type the result + unit →
 * Save. Each save (a) feeds `useTestKitLogs().addLog` so quick + guided
 * entries appear together in the Logs tab, and (b) writes a durable copy
 * into the server-backed `test-results` namespace (so results survive a
 * browser clear / device switch and live on the Pi per architecture D1).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ALL_TEST_KITS } from '@reefapp/shared';
import type { ParameterKey, TestKit } from '@reefapp/shared';

import { useServerBackedState } from '../../hooks/useServerBackedState';
import { useTestKitLogs } from '../../test-kits/useTestKitLogs';

/** A persisted quick-log result row (durable copy on the Pi). */
export interface QuickLogResult {
  readonly id: string;
  /** ISO 8601 timestamp. */
  readonly date: string;
  readonly parameterKey: ParameterKey;
  readonly parameter: string;
  readonly kitSlug: string;
  readonly brand: string;
  readonly testName: string;
  readonly result: number;
  readonly unit: string;
  readonly notes?: string;
}

/** Shape of the `test-results` server-backed namespace blob. */
export interface QuickLogStore {
  /** parameterKey → last-used kit slug (so the picker remembers). */
  readonly lastKitByParam: Readonly<Record<string, string>>;
  /** Newest-first durable copies of quick-log results. */
  readonly entries: readonly QuickLogResult[];
}

const QUICK_LOG_NAMESPACE = 'test-results';

export const EMPTY_STORE: QuickLogStore = { lastKitByParam: {}, entries: [] };

/** Preferred quick-entry parameters, in display order. */
const QUICK_PARAMS: ReadonlyArray<{ key: ParameterKey; label: string }> = [
  { key: 'alkalinity', label: 'Alkalinity' },
  { key: 'calcium', label: 'Calcium' },
  { key: 'magnesium', label: 'Magnesium' },
  { key: 'nitrate', label: 'Nitrate' },
  { key: 'phosphate', label: 'Phosphate' },
  { key: 'ph', label: 'pH' },
  { key: 'salinity', label: 'Salinity' },
  { key: 'ammonia', label: 'Ammonia' },
  { key: 'nitrite', label: 'Nitrite' },
];

function groupKitsByParam(): Map<ParameterKey, TestKit[]> {
  const map = new Map<ParameterKey, TestKit[]>();
  for (const kit of ALL_TEST_KITS) {
    const list = map.get(kit.parameterKey);
    if (list) list.push(kit);
    else map.set(kit.parameterKey, [kit]);
  }
  return map;
}

export function QuickLogPanel() {
  const { addLog } = useTestKitLogs();
  const { value: store, setValue: setStore } =
    useServerBackedState<QuickLogStore>(QUICK_LOG_NAMESPACE, EMPTY_STORE);

  const kitsByParam = useMemo(groupKitsByParam, []);

  const quickParams = useMemo(
    () => QUICK_PARAMS.filter((p) => (kitsByParam.get(p.key)?.length ?? 0) > 0),
    [kitsByParam],
  );

  const [selectedParam, setSelectedParam] = useState<ParameterKey | null>(null);
  const [selectedKitSlug, setSelectedKitSlug] = useState<string>('');
  const [resultText, setResultText] = useState('');
  const [unit, setUnit] = useState('');
  const [notes, setNotes] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);

  const kitBySlug = useCallback(
    (slug: string): TestKit | undefined =>
      ALL_TEST_KITS.find((k) => k.slug === slug),
    [],
  );

  /** Pick the default kit for a parameter (remembered, else first). */
  const defaultKitForParam = useCallback(
    (param: ParameterKey): TestKit | undefined => {
      const kits = kitsByParam.get(param);
      if (!kits || kits.length === 0) return undefined;
      const remembered = store.lastKitByParam[param];
      const found = remembered
        ? kits.find((k) => k.slug === remembered)
        : undefined;
      return found ?? kits[0];
    },
    [kitsByParam, store.lastKitByParam],
  );

  // Initialize the selection once (after the store + catalog are ready).
  useEffect(() => {
    if (selectedParam !== null || quickParams.length === 0) return;
    const first = quickParams[0];
    if (!first) return;
    const kit = defaultKitForParam(first.key);
    setSelectedParam(first.key);
    setSelectedKitSlug(kit?.slug ?? '');
    setUnit(kit?.resultUnit ?? '');
  }, [selectedParam, quickParams, defaultKitForParam]);

  const handleSelectParam = useCallback(
    (param: ParameterKey) => {
      const kit = defaultKitForParam(param);
      setSelectedParam(param);
      setSelectedKitSlug(kit?.slug ?? '');
      setUnit(kit?.resultUnit ?? '');
      setResultText('');
      setNotes('');
      setSavedFlash(false);
    },
    [defaultKitForParam],
  );

  const handleSelectKit = useCallback(
    (slug: string) => {
      setSelectedKitSlug(slug);
      const kit = kitBySlug(slug);
      setUnit(kit?.resultUnit ?? '');
    },
    [kitBySlug],
  );

  const parsedResult = Number(resultText);
  const canSave =
    resultText.trim() !== '' &&
    Number.isFinite(parsedResult) &&
    selectedKitSlug !== '';

  const handleSave = useCallback(() => {
    if (!canSave) return;
    const kit = kitBySlug(selectedKitSlug);
    if (!kit) return;
    const trimmedUnit = unit.trim();
    const trimmedNotes = notes.trim();

    // (a) Feed the shared store the guided flow uses (Logs tab history).
    addLog({
      brand: kit.brand,
      testName: kit.name,
      kitSlug: kit.slug,
      parameter: kit.parameter,
      parameterKey: kit.parameterKey,
      result: parsedResult,
      unit: trimmedUnit,
      ...(trimmedNotes ? { notes: trimmedNotes } : {}),
    });

    // (b) Durable copy on the Pi + remember this kit for the parameter.
    const entry: QuickLogResult = {
      id: crypto.randomUUID(),
      date: new Date().toISOString(),
      parameterKey: kit.parameterKey,
      parameter: kit.parameter,
      kitSlug: kit.slug,
      brand: kit.brand,
      testName: kit.name,
      result: parsedResult,
      unit: trimmedUnit,
      ...(trimmedNotes ? { notes: trimmedNotes } : {}),
    };
    setStore({
      lastKitByParam: { ...store.lastKitByParam, [kit.parameterKey]: kit.slug },
      entries: [entry, ...store.entries],
    });

    setResultText('');
    setNotes('');
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 2000);
  }, [
    canSave,
    kitBySlug,
    selectedKitSlug,
    unit,
    notes,
    parsedResult,
    addLog,
    setStore,
    store.lastKitByParam,
    store.entries,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') handleSave();
    },
    [handleSave],
  );

  const activeKits = selectedParam
    ? (kitsByParam.get(selectedParam) ?? [])
    : [];

  return (
    <div className="reefapp-quick-log space-y-6 max-w-2xl mx-auto">
      <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 shadow-lg">
        <h2 className="text-xl font-bold text-cyan-300 mb-1">Quick Log</h2>
        <p className="text-sm text-slate-400 mb-5">
          Record a result in a few taps. Entries appear in the Logs tab
          alongside guided tests and are saved on your controller.
        </p>

        {/* Step 1 — parameter */}
        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
          1. Parameter
        </label>
        <div className="flex flex-wrap gap-2 mb-6">
          {quickParams.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => handleSelectParam(p.key)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                selectedParam === p.key
                  ? 'bg-cyan-600 text-white shadow'
                  : 'bg-slate-700 text-slate-200 hover:bg-slate-600'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Step 2 — kit */}
        <label
          htmlFor="quick-log-kit"
          className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2"
        >
          2. Test kit
        </label>
        <select
          id="quick-log-kit"
          aria-label="Test kit"
          value={selectedKitSlug}
          onChange={(e) => handleSelectKit(e.target.value)}
          disabled={activeKits.length === 0}
          className="w-full mb-6 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
        >
          {activeKits.map((k) => (
            <option key={k.slug} value={k.slug}>
              {k.brand} — {k.name}
            </option>
          ))}
        </select>

        {/* Step 3 — result + unit */}
        <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
          3. Result
        </label>
        <div className="flex gap-3 mb-4">
          <input
            type="number"
            inputMode="decimal"
            aria-label="Result"
            value={resultText}
            onChange={(e) => setResultText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="0.0"
            className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
          <input
            type="text"
            aria-label="Unit"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            placeholder="unit"
            className="w-28 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>

        {/* Optional notes */}
        <input
          type="text"
          aria-label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (optional)"
          className="w-full mb-5 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500"
        />

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="px-6 py-2 rounded-lg text-sm font-semibold bg-cyan-600 text-white shadow hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Save
          </button>
          {savedFlash && (
            <span className="text-sm font-semibold text-emerald-400">
              Saved ✓
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
